import { useState, useRef, useEffect, useCallback } from "react";
import { api, useAuthStore, useVoiceStore, useUserPreferencesStore, useAppConfigStore, validateSizedFile, useServerConfigStore, useXenForoStore, getApiBase, setApiBase, getStorage, isElectron, parseCosmeticCss, CosmeticRarityNames, CosmeticRarityColors, CosmeticTypeNames, CosmeticType, parseValidationErrors, getFieldError } from "@abyss/shared";
import type { UserCosmetic, CosmeticItem } from "@abyss/shared";
import { Capacitor } from "@capacitor/core";
import axios from "axios";
import { formatKeybind, captureKeybindFromEvent } from "../utils/keybind";
import AudioTrimmer from "./AudioTrimmer";
import { describeUploadError } from "../utils/uploadErrors";
import { VoiceDebugPanel } from "./VoiceDebugPanel";
import { SCREEN_OPTIONS } from "../constants/screenShareTiers";
import SettingsModal from "./SettingsModal";
import { isMobile } from "../stores/mobileStore";
import type { SettingsTab as SettingsTabDef } from "./SettingsModal";

type ActiveTab = "profile" | "voice" | "video" | "keybinds" | "cosmetics" | "connections" | "app" | "server" | "account";

export default function UserSettingsModal({
  onClose,
}: {
  onClose: () => void;
}) {
  const user = useAuthStore((s) => s.user)!;
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const updateAvatar = useAuthStore((s) => s.updateAvatar);
  const logout = useAuthStore((s) => s.logout);

  const [activeTab, setActiveTab] = useState<ActiveTab>("profile");

  // Check if we're on production web (not localhost, not Capacitor, not Electron)
  const isProductionWeb = typeof window !== 'undefined' &&
    !window.location.hostname.includes('localhost') &&
    !window.location.hostname.includes('127.0.0.1') &&
    !Capacitor.isNativePlatform() &&
    typeof window.electron === 'undefined';

  const settingsTabs: SettingsTabDef[] = [
    { id: "profile", label: "Profile" },
    { id: "voice", label: "Voice & Audio" },
    { id: "video", label: "Video" },
    { id: "keybinds", label: "Keybinds" },
    { id: "cosmetics", label: "Cosmetics" },
    { id: "connections", label: "Connections", separatorBefore: true },
    { id: "app", label: "App Settings", visible: isElectron() },
    { id: "server", label: "Instance", visible: !isProductionWeb, separatorBefore: !isElectron() },
    { id: "account", label: "Account" },
  ];

  const [displayName, setDisplayName] = useState(user.displayName);
  const [bio, setBio] = useState(user.bio || "");
  const [status, setStatus] = useState(user.status);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileFieldErrors, setProfileFieldErrors] = useState<Record<string, string[]> | null>(null);
  const [capturingKey, setCapturingKey] = useState(false);
  const [capturingKeybind, setCapturingKeybind] = useState<string | null>(null);
  const [micTestActive, setMicTestActive] = useState(false);
  const [micTestLevel, setMicTestLevel] = useState(0);
  const [micTestRecording, setMicTestRecording] = useState(false);
  const [micTestPlaybackUrl, setMicTestPlaybackUrl] = useState<string | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const micTestStreamRef = useRef<MediaStream | null>(null);
  const micTestCtxRef = useRef<AudioContext | null>(null);
  const micTestAnalyserRef = useRef<AnalyserNode | null>(null);
  const micTestSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const micTestRafRef = useRef<number | null>(null);
  const micTestRecorderRef = useRef<MediaRecorder | null>(null);

  const voiceMode = useVoiceStore((s) => s.voiceMode);
  const pttKey = useVoiceStore((s) => s.pttKey);
  const setVoiceMode = useVoiceStore((s) => s.setVoiceMode);
  const setPttKey = useVoiceStore((s) => s.setPttKey);
  const inputDeviceId = useVoiceStore((s) => s.inputDeviceId);
  const outputDeviceId = useVoiceStore((s) => s.outputDeviceId);
  const setInputDeviceId = useVoiceStore((s) => s.setInputDeviceId);
  const setOutputDeviceId = useVoiceStore((s) => s.setOutputDeviceId);
  const noiseSuppression = useVoiceStore((s) => s.noiseSuppression);
  const echoCancellation = useVoiceStore((s) => s.echoCancellation);
  const autoGainControl = useVoiceStore((s) => s.autoGainControl);
  const setNoiseSuppression = useVoiceStore((s) => s.setNoiseSuppression);
  const setEchoCancellation = useVoiceStore((s) => s.setEchoCancellation);
  const setAutoGainControl = useVoiceStore((s) => s.setAutoGainControl);
  const inputSensitivity = useVoiceStore((s) => s.inputSensitivity);
  const setInputSensitivity = useVoiceStore((s) => s.setInputSensitivity);
  const cameraDeviceId = useVoiceStore((s) => s.cameraDeviceId);
  const screenShareQuality = useVoiceStore((s) => s.screenShareQuality);
  const setScreenShareQuality = useVoiceStore((s) => s.setScreenShareQuality);
  const setCameraDeviceId = useVoiceStore((s) => s.setCameraDeviceId);
  const voiceChatDesktopNotify = useVoiceStore((s) => s.voiceChatDesktopNotify);
  const setVoiceChatDesktopNotify = useVoiceStore((s) => s.setVoiceChatDesktopNotify);
  const nativeDesktopNotificationsEnabled = useVoiceStore((s) => s.nativeDesktopNotificationsEnabled);
  const setNativeDesktopNotificationsEnabled = useVoiceStore((s) => s.setNativeDesktopNotificationsEnabled);
  const keybindToggleMute = useVoiceStore((s) => s.keybindToggleMute);
  const keybindToggleDeafen = useVoiceStore((s) => s.keybindToggleDeafen);
  const keybindDisconnect = useVoiceStore((s) => s.keybindDisconnect);
  const setKeybindToggleMute = useVoiceStore((s) => s.setKeybindToggleMute);
  const setKeybindToggleDeafen = useVoiceStore((s) => s.setKeybindToggleDeafen);
  const setKeybindDisconnect = useVoiceStore((s) => s.setKeybindDisconnect);

  const preferences = useUserPreferencesStore((s) => s.preferences);
  const uploadSound = useUserPreferencesStore((s) => s.uploadSound);
  const uploadLimits = useAppConfigStore((s) => s.uploadLimits);
  const removeSound = useUserPreferencesStore((s) => s.removeSound);
  const [soundUploading, setSoundUploading] = useState<'join' | 'leave' | null>(null);
  const [soundError, setSoundError] = useState<string | null>(null);
  const joinSoundInputRef = useRef<HTMLInputElement>(null);
  const leaveSoundInputRef = useRef<HTMLInputElement>(null);

  // Auto-launch state
  const [autoLaunchEnabled, setAutoLaunchEnabled] = useState(false);
  const [autoLaunchLoading, setAutoLaunchLoading] = useState(false);

  // Instance configuration state
  const serverUrl = useServerConfigStore((s) => s.serverUrl);
  const recentInstances = useServerConfigStore((s) => s.recentInstances);
  const removeRecentInstance = useServerConfigStore((s) => s.removeRecentInstance);
  const setStoredServerUrl = useServerConfigStore((s) => s.setServerUrl);
  const [serverUrlInput, setServerUrlInput] = useState(serverUrl || import.meta.env.VITE_API_URL || '');
  const [serverUrlSaving, setServerUrlSaving] = useState(false);
  const [serverUrlError, setServerUrlError] = useState<string | null>(null);

  // Cosmetics state
  const [myCosmetics, setMyCosmetics] = useState<UserCosmetic[]>([]);
  const [cosmeticsLoading, setCosmeticsLoading] = useState(false);
  const [cosmeticsLoaded, setCosmeticsLoaded] = useState(false);
  const [cosmeticError, setCosmeticError] = useState<string | null>(null);
  const [cosmeticTypeFilter, setCosmeticTypeFilter] = useState<number>(-1);

  // XenForo connection state
  const xfConnection = useXenForoStore((s) => s.connection);
  const xfConnectionLoaded = useXenForoStore((s) => s.connectionLoaded);
  const fetchXfConnection = useXenForoStore((s) => s.fetchConnection);
  const unlinkXf = useXenForoStore((s) => s.unlink);
  const [xfBusy, setXfBusy] = useState(false);
  const [xfError, setXfError] = useState<string | null>(null);

  const [inputDevices, setInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [outputDevices, setOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [cameraTestActive, setCameraTestActive] = useState(false);
  const [cameraTestStream, setCameraTestStream] = useState<MediaStream | null>(
    null,
  );
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const canSelectOutput =
    typeof (
      HTMLMediaElement.prototype as HTMLMediaElement & {
        setSinkId?: (id: string) => Promise<void>;
      }
    ).setSinkId === "function";

  useEffect(() => {
    if (!capturingKey) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      setPttKey(e.key);
      setCapturingKey(false);
    };
    const onMouse = (e: MouseEvent) => {
      // Only capture non-primary buttons (skip left click = 0, right click = 2)
      if (e.button === 0 || e.button === 2) return;
      e.preventDefault();
      setPttKey(`Mouse${e.button}`);
      setCapturingKey(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onMouse);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onMouse);
    };
  }, [capturingKey, setPttKey]);

  const keybindSetters: Record<string, (bind: string) => void> = {
    toggleMute: setKeybindToggleMute,
    toggleDeafen: setKeybindToggleDeafen,
    disconnect: setKeybindDisconnect,
  };

  const keybindDefaults: Record<string, string> = {
    toggleMute: "mod+shift+m",
    toggleDeafen: "mod+shift+d",
    disconnect: "mod+shift+e",
  };

  useEffect(() => {
    if (!capturingKeybind) return;
    // Capture phase + stopPropagation, so the keypress never reaches the app's
    // own shortcuts while we're recording. That means Escape has to be handled
    // here too — a separate bubble-phase listener would never see it.
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === "Escape") {
        setCapturingKeybind(null);
        return;
      }
      const bind = captureKeybindFromEvent(e);
      if (!bind) return;
      keybindSetters[capturingKeybind](bind);
      setCapturingKeybind(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [capturingKeybind, setKeybindToggleMute, setKeybindToggleDeafen, setKeybindDisconnect]);

  useEffect(() => {
    if (!micTestActive) return;
    if (!navigator.mediaDevices?.getUserMedia) return;

    let cancelled = false;
    const startTest = async () => {
      if (micTestRafRef.current) cancelAnimationFrame(micTestRafRef.current);
      micTestRafRef.current = null;
      if (micTestSourceRef.current) micTestSourceRef.current.disconnect();
      micTestSourceRef.current = null;
      if (micTestCtxRef.current && micTestCtxRef.current.state !== "closed")
        micTestCtxRef.current.close();
      micTestCtxRef.current = null;
      if (micTestStreamRef.current)
        micTestStreamRef.current.getTracks().forEach((track) => track.stop());
      micTestStreamRef.current = null;
      micTestAnalyserRef.current = null;
      setMicTestLevel(0);

      try {
        const constraints: MediaStreamConstraints = {
          audio: {
            noiseSuppression,
            echoCancellation,
            autoGainControl,
            ...(inputDeviceId && inputDeviceId !== "default"
              ? { deviceId: { exact: inputDeviceId } }
              : {}),
          },
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const ctx = new AudioContext();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);

        micTestStreamRef.current = stream;
        micTestCtxRef.current = ctx;
        micTestSourceRef.current = source;
        micTestAnalyserRef.current = analyser;

        const buffer = new Uint8Array(256);
        const loop = () => {
          if (!micTestAnalyserRef.current) return;
          micTestAnalyserRef.current.getByteTimeDomainData(buffer);
          let sum = 0;
          for (let i = 0; i < buffer.length; i++) {
            const val = (buffer[i] - 128) / 128;
            sum += val * val;
          }
          const rms = Math.sqrt(sum / buffer.length);
          setMicTestLevel(rms);
          micTestRafRef.current = requestAnimationFrame(loop);
        };
        loop();
      } catch (err) {
        console.error("Failed to start mic test", err);
        setMicTestActive(false);
      }
    };

    startTest();

    return () => {
      cancelled = true;
    };
  }, [
    micTestActive,
    noiseSuppression,
    echoCancellation,
    autoGainControl,
    inputDeviceId,
  ]);

  useEffect(() => {
    return () => {
      if (micTestRafRef.current) cancelAnimationFrame(micTestRafRef.current);
      if (micTestSourceRef.current) micTestSourceRef.current.disconnect();
      if (micTestCtxRef.current && micTestCtxRef.current.state !== "closed")
        micTestCtxRef.current.close();
      if (micTestStreamRef.current)
        micTestStreamRef.current.getTracks().forEach((track) => track.stop());
      if (micTestPlaybackUrl) URL.revokeObjectURL(micTestPlaybackUrl);
    };
  }, [micTestPlaybackUrl]);

  const stopMicTest = () => {
    if (micTestRecorderRef.current) {
      micTestRecorderRef.current.stop();
      micTestRecorderRef.current = null;
    }
    if (micTestRafRef.current) cancelAnimationFrame(micTestRafRef.current);
    micTestRafRef.current = null;
    if (micTestSourceRef.current) micTestSourceRef.current.disconnect();
    micTestSourceRef.current = null;
    if (micTestCtxRef.current && micTestCtxRef.current.state !== "closed")
      micTestCtxRef.current.close();
    micTestCtxRef.current = null;
    if (micTestStreamRef.current)
      micTestStreamRef.current.getTracks().forEach((track) => track.stop());
    micTestStreamRef.current = null;
    micTestAnalyserRef.current = null;
    setMicTestLevel(0);
    setMicTestActive(false);
  };

  const runMicCheck = async () => {
    if (micTestRecording) return;
    setMicTestRecording(true);
    if (micTestPlaybackUrl) {
      URL.revokeObjectURL(micTestPlaybackUrl);
      setMicTestPlaybackUrl(null);
    }

    let streamToUse = micTestStreamRef.current;
    let cleanupStream = false;
    try {
      if (!streamToUse) {
        const constraints: MediaStreamConstraints = {
          audio: {
            noiseSuppression,
            echoCancellation,
            autoGainControl,
            ...(inputDeviceId && inputDeviceId !== "default"
              ? { deviceId: { exact: inputDeviceId } }
              : {}),
          },
        };
        streamToUse = await navigator.mediaDevices.getUserMedia(constraints);
        cleanupStream = true;
      }

      const recorder = new MediaRecorder(streamToUse);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks, {
          type: recorder.mimeType || "audio/webm",
        });
        const url = URL.createObjectURL(blob);
        setMicTestPlaybackUrl(url);
        setMicTestRecording(false);
        if (cleanupStream && streamToUse) {
          streamToUse.getTracks().forEach((track) => track.stop());
        }
      };
      micTestRecorderRef.current = recorder;
      recorder.start();
      setTimeout(() => {
        if (recorder.state !== "inactive") recorder.stop();
      }, 4000);
    } catch (err) {
      console.error("Mic check failed", err);
      setMicTestRecording(false);
      if (cleanupStream && streamToUse) {
        streamToUse.getTracks().forEach((track) => track.stop());
      }
    }
  };

  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    let active = true;

    const loadDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!active) return;
        setInputDevices(
          devices.filter((device) => device.kind === "audioinput"),
        );
        setOutputDevices(
          devices.filter((device) => device.kind === "audiooutput"),
        );
        setVideoDevices(
          devices.filter((device) => device.kind === "videoinput"),
        );
        setDeviceError(null);
      } catch (err) {
        console.error("Failed to enumerate devices", err);
        if (active) setDeviceError("Unable to list audio devices.");
      }
    };

    loadDevices();
    navigator.mediaDevices.addEventListener("devicechange", loadDevices);
    return () => {
      active = false;
      navigator.mediaDevices.removeEventListener("devicechange", loadDevices);
    };
  }, []);

  // Load auto-launch status when app tab is opened
  useEffect(() => {
    if (activeTab === "app" && isElectron() && window.electron?.autoLaunch) {
      setAutoLaunchLoading(true);
      window.electron.autoLaunch.isEnabled()
        .then(enabled => setAutoLaunchEnabled(enabled))
        .catch(err => console.error("Failed to check auto-launch status:", err))
        .finally(() => setAutoLaunchLoading(false));
    }
  }, [activeTab]);

  const renderDeviceLabel = (
    device: MediaDeviceInfo,
    index: number,
    kind: "input" | "output",
  ) => {
    if (device.deviceId === "default") return "Default";
    if (device.label) return device.label;
    return kind === "input"
      ? `Microphone ${index + 1}`
      : `Speaker ${index + 1}`;
  };

  const startCameraTest = useCallback(async () => {
    // Stop any existing test stream
    if (cameraTestStream) {
      cameraTestStream.getTracks().forEach((t) => t.stop());
    }
    try {
      const videoConstraints: MediaTrackConstraints = {
        width: { ideal: 640 },
        height: { ideal: 480 },
      };
      if (cameraDeviceId && cameraDeviceId !== "default") {
        videoConstraints.deviceId = { exact: cameraDeviceId };
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false,
      });
      setCameraTestStream(stream);
      setCameraTestActive(true);
    } catch (err) {
      console.error("Failed to start camera test:", err);
      setCameraTestActive(false);
    }
  }, [cameraDeviceId, cameraTestStream]);

  const stopCameraTest = useCallback(() => {
    if (cameraTestStream) {
      cameraTestStream.getTracks().forEach((t) => t.stop());
      setCameraTestStream(null);
    }
    setCameraTestActive(false);
  }, [cameraTestStream]);

  // Attach camera stream to video element
  useEffect(() => {
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = cameraTestStream;
    }
  }, [cameraTestStream]);

  // Restart camera test when device changes (if test is active)
  useEffect(() => {
    if (!cameraTestActive) return;
    let cancelled = false;
    (async () => {
      try {
        const videoConstraints: MediaTrackConstraints = {
          width: { ideal: 640 },
          height: { ideal: 480 },
        };
        if (cameraDeviceId && cameraDeviceId !== "default") {
          videoConstraints.deviceId = { exact: cameraDeviceId };
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        // Stop old stream
        setCameraTestStream((prev) => {
          if (prev) prev.getTracks().forEach((t) => t.stop());
          return stream;
        });
      } catch (err) {
        console.error("Failed to switch camera:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cameraDeviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup camera test on unmount
  useEffect(() => {
    return () => {
      setCameraTestStream((prev) => {
        if (prev) prev.getTracks().forEach((t) => t.stop());
        return null;
      });
    };
  }, []);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const check = validateSizedFile(file, uploadLimits.avatarMaxSize, 'Avatars');
    if (!check.ok) {
      setProfileError(check.reason);
      e.target.value = '';
      return;
    }
    setProfileError(null);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    setProfileError(null);
    setProfileFieldErrors(null);
    try {
      if (avatarFile) {
        await updateAvatar(avatarFile);
      }
      await updateProfile({ displayName, bio, status });
      onClose();
    } catch (err: any) {
      const parsed = parseValidationErrors(err);
      if (parsed) {
        setProfileFieldErrors(parsed);
        // Build a summary from all field errors
        const messages = Object.values(parsed).flat();
        if (messages.length > 0) setProfileError(messages.join(', '));
      } else {
        setProfileError(err.response?.data || 'Failed to update profile');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAutoLaunchToggle = async (enabled: boolean) => {
    if (!window.electron?.autoLaunch) return;
    setAutoLaunchLoading(true);
    try {
      await window.electron.autoLaunch.setEnabled(enabled);
      setAutoLaunchEnabled(enabled);
    } catch (err) {
      console.error("Failed to toggle auto-launch:", err);
      alert("Failed to change auto-launch setting.");
    } finally {
      setAutoLaunchLoading(false);
    }
  };

  const handleServerUrlSave = async (urlOverride?: string) => {
    setServerUrlError(null);
    setServerUrlSaving(true);

    try {
      const trimmed = (urlOverride || serverUrlInput).trim();
      if (!trimmed) {
        setServerUrlError('Please enter a server URL');
        setServerUrlSaving(false);
        return;
      }

      // Test if the server is reachable
      const testUrl = `${trimmed}/health`;
      try {
        await axios.get(testUrl, { timeout: 5000 });
      } catch (err: any) {
        if (err.code === 'ECONNABORTED') {
          setServerUrlError('Server connection timed out. Please check the URL.');
          setServerUrlSaving(false);
          return;
        } else if (err.response) {
          // Server responded - that's good enough (CORS errors still mean server is reachable)
          console.log('Server is reachable (status:', err.response.status, ')');
        } else if (err.message?.includes('Network Error') || err.message?.includes('CORS')) {
          // CORS error means server exists but doesn't allow app://abyss origin - that's fine
          console.log('Server detected (CORS blocked but server is reachable)');
        } else {
          setServerUrlError('Unable to connect to server. Please check the URL.');
          setServerUrlSaving(false);
          return;
        }
      }

      // Save and apply
      setStoredServerUrl(trimmed);
      setApiBase(trimmed);

      // Restart app if in Electron, otherwise show message
      if (isElectron() && window.electron?.restartApp) {
        window.electron.restartApp();
      } else {
        alert('Server URL updated successfully. Please restart the app for all changes to take effect.');
      }
    } catch (err) {
      console.error('Server URL save error:', err);
      setServerUrlError('Failed to update server URL');
    } finally {
      setServerUrlSaving(false);
    }
  };

  const [trimmerFile, setTrimmerFile] = useState<File | null>(null);
  const [trimmerType, setTrimmerType] = useState<'join' | 'leave' | null>(null);

  const handleSoundFileSelect = (type: 'join' | 'leave', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setSoundError(null);

    // Limit comes from the server via /api/config so it can't drift from
    // MediaConfig.SoundMaxSize.
    const check = validateSizedFile(file, uploadLimits.soundMaxSize, 'Sounds');
    if (!check.ok) {
      setSoundError(check.reason);
      return;
    }

    setTrimmerFile(file);
    setTrimmerType(type);
  };

  const handleTrimmerConfirm = async (trimmedFile: File) => {
    if (!trimmerType) return;
    setTrimmerFile(null);
    setTrimmerType(null);
    setSoundUploading(trimmerType);
    try {
      await uploadSound(trimmerType, trimmedFile);
    } catch (err: unknown) {
      setSoundError(describeUploadError(err));
    } finally {
      setSoundUploading(null);
    }
  };

  const handleTrimmerCancel = () => {
    setTrimmerFile(null);
    setTrimmerType(null);
  };

  const handleSoundRemove = async (type: 'join' | 'leave') => {
    setSoundError(null);
    setSoundUploading(type);
    try {
      await removeSound(type);
    } catch {
      setSoundError('Failed to remove sound');
    } finally {
      setSoundUploading(null);
    }
  };

  // Cosmetics logic
  const loadMyCosmetics = async () => {
    setCosmeticsLoading(true);
    setCosmeticError(null);
    try {
      const res = await api.get('/cosmetics/my');
      setMyCosmetics(res.data);
      setCosmeticsLoaded(true);
    } catch (err: any) {
      setCosmeticError(err?.response?.data || 'Failed to load cosmetics.');
    } finally {
      setCosmeticsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'cosmetics' && !cosmeticsLoaded && !cosmeticsLoading) {
      loadMyCosmetics();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'connections' && !xfConnectionLoaded) {
      fetchXfConnection();
    }
  }, [activeTab, xfConnectionLoaded, fetchXfConnection]);

  const handleLinkXenForo = () => {
    const token = getStorage().getItem('token');
    if (!token) {
      setXfError('You must be logged in to link an account.');
      return;
    }
    // Desktop's window.location.origin (file:// or app://) isn't navigable from
    // an external browser, so route those users to a hosted success page instead.
    const apiBase = getApiBase() || window.location.origin;
    const returnUrl = window.electron
      ? `${apiBase}/api/xenforo/link/done`
      : `${window.location.origin}${window.location.pathname}`;
    const url = `${getApiBase()}/api/xenforo/link/start`
      + `?return=${encodeURIComponent(returnUrl)}`
      + `&access_token=${encodeURIComponent(token)}`;
    window.location.href = url;
  };

  const handleUnlinkXenForo = async () => {
    setXfError(null);
    setXfBusy(true);
    try {
      await unlinkXf();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      let msg: string | undefined;
      if (typeof data === 'string') msg = data;
      else if (data && typeof data === 'object') {
        const o = data as { detail?: string; title?: string };
        msg = o.detail || o.title;
      }
      setXfError(msg || 'Failed to unlink XenForo account.');
    } finally {
      setXfBusy(false);
    }
  };

  const handleEquip = async (cosmeticItemId: string) => {
    setCosmeticError(null);
    try {
      const res = await api.put('/cosmetics/equip', { cosmeticItemId });
      // Update local list
      const equipped = res.data;
      setMyCosmetics((prev) => prev.map((uc) => {
        const equippedItem = [equipped.nameplate, equipped.messageStyle, equipped.profileEffect, equipped.avatarDecoration].find((e: CosmeticItem | null) => e?.id === uc.item.id);
        if (equippedItem) return { ...uc, isEquipped: true };
        // Unequip others of same type if this was just equipped
        const targetItem = prev.find((c) => c.item.id === cosmeticItemId);
        if (targetItem && uc.item.type === targetItem.item.type && uc.item.id !== cosmeticItemId) return { ...uc, isEquipped: false };
        return uc;
      }));
      // Update auth store so cosmetics show across the app
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        const updatedUser = { ...currentUser, cosmetics: equipped };
        getStorage().setItem('user', JSON.stringify(updatedUser));
        useAuthStore.setState({ user: updatedUser });
      }
    } catch (err: any) {
      setCosmeticError(err?.response?.data || 'Failed to equip cosmetic.');
    }
  };

  const handleUnequip = async (cosmeticItemId: string) => {
    setCosmeticError(null);
    try {
      const res = await api.put(`/cosmetics/unequip/${cosmeticItemId}`);
      const equipped = res.data;
      setMyCosmetics((prev) => prev.map((uc) => uc.item.id === cosmeticItemId ? { ...uc, isEquipped: false } : uc));
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        const updatedUser = { ...currentUser, cosmetics: equipped };
        getStorage().setItem('user', JSON.stringify(updatedUser));
        useAuthStore.setState({ user: updatedUser });
      }
    } catch (err: any) {
      setCosmeticError(err?.response?.data || 'Failed to unequip cosmetic.');
    }
  };

  const filteredMyCosmetics = (cosmeticTypeFilter >= 0
    ? myCosmetics.filter((uc) => uc.item.type === cosmeticTypeFilter)
    : myCosmetics
  ).slice().sort((a: UserCosmetic, b: UserCosmetic) => b.item.rarity - a.item.rarity);

  const renderCosmeticPreview = (item: CosmeticItem) => {
    if (item.type === CosmeticType.Nameplate) {
      const style = parseCosmeticCss(item.cssData, 'nameplate');
      return <span className="cosmetic-preview-name" style={style}>{item.name}</span>;
    }
    if (item.type === CosmeticType.MessageStyle) {
      const style = parseCosmeticCss(item.cssData, 'messageStyle');
      return (
        <div className="cosmetic-preview-msg" style={style}>
          <div className="cosmetic-preview-msg-author">SampleUser</div>
          <div className="cosmetic-preview-msg-text">Preview message with this style.</div>
        </div>
      );
    }
    return <span className="cosmetic-preview-name">{item.name}</span>;
  };

  const currentAvatar =
    avatarPreview ||
    (user.avatarUrl
      ? user.avatarUrl.startsWith("http")
        ? user.avatarUrl
        : `${getApiBase()}${user.avatarUrl}`
      : null);

  return (
    <SettingsModal
      title="Settings"
      tabs={settingsTabs}
      activeTab={activeTab}
      onTabChange={(id) => setActiveTab(id as ActiveTab)}
      onClose={onClose}
    >
            {activeTab === "profile" && (
              <>
                {profileError && <div className="auth-error">{profileError}</div>}
                <div className="us-card">
                  <div className="settings-avatar-section">
                    <div
                      className="settings-avatar"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {currentAvatar ? (
                        <img src={currentAvatar} alt={user.displayName} />
                      ) : (
                        <span className="settings-avatar-letter">
                          {user.displayName.charAt(0).toUpperCase()}
                        </span>
                      )}
                      <div className="settings-avatar-overlay">Change</div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleAvatarChange}
                    />
                  </div>

                  <label className={getFieldError(profileFieldErrors, 'DisplayName') ? 'has-error' : ''}>
                    Display Name
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      maxLength={32}
                    />
                    {getFieldError(profileFieldErrors, 'DisplayName') && (
                      <span className="field-error">{getFieldError(profileFieldErrors, 'DisplayName')}</span>
                    )}
                  </label>

                  <label className={getFieldError(profileFieldErrors, 'Bio') ? 'has-error' : ''}>
                    Bio
                    <textarea
                      className="settings-textarea"
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      maxLength={500}
                      rows={3}
                      placeholder="Tell us about yourself"
                    />
                    {getFieldError(profileFieldErrors, 'Bio') && (
                      <span className="field-error">{getFieldError(profileFieldErrors, 'Bio')}</span>
                    )}
                  </label>

                  <label className={getFieldError(profileFieldErrors, 'Status') ? 'has-error' : ''}>
                    Status
                    <input
                      type="text"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      maxLength={128}
                    />
                    {getFieldError(profileFieldErrors, 'Status') && (
                      <span className="field-error">{getFieldError(profileFieldErrors, 'Status')}</span>
                    )}
                  </label>
                </div>

                <div className="us-card-actions">
                  <button className="btn-secondary" onClick={onClose}>
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </>
            )}

            {activeTab === "voice" && (
              <>
                {!isMobile() && (
                  <div className="us-card">
                    <div className="us-card-title">Voice Mode</div>
                    <div className="channel-type-select">
                      <button
                        type="button"
                        className={`type-option ${voiceMode === "voice-activity" ? "active" : ""}`}
                        onClick={() => setVoiceMode("voice-activity")}
                      >
                        Voice Activity
                      </button>
                      <button
                        type="button"
                        className={`type-option ${voiceMode === "push-to-talk" ? "active" : ""}`}
                        onClick={() => setVoiceMode("push-to-talk")}
                      >
                        Push to Talk
                      </button>
                    </div>

                    {voiceMode === "push-to-talk" && (
                      <label style={{ marginTop: 12 }}>
                        PTT Key
                        <button
                          type="button"
                          className={`ptt-key-capture ${capturingKey ? "recording" : ""}`}
                          onClick={() => setCapturingKey(true)}
                        >
                          {capturingKey
                            ? "Press any key or mouse button..."
                            : pttKey.startsWith("Mouse")
                              ? `Mouse Button ${pttKey.slice(5)}`
                              : pttKey}
                        </button>
                      </label>
                    )}
                  </div>
                )}

                <div className="us-card">
                  <div className="us-card-title">Audio Processing</div>
                  <label className="settings-toggle">
                    <input
                      type="checkbox"
                      checked={noiseSuppression}
                      onChange={(e) => setNoiseSuppression(e.target.checked)}
                    />
                    ANTI WILL BREATHING TECHNOLOGY
                  </label>
                  <label className="settings-toggle">
                    <input
                      type="checkbox"
                      checked={echoCancellation}
                      onChange={(e) => setEchoCancellation(e.target.checked)}
                    />
                    Echo Cancellation
                  </label>
                  <label className="settings-toggle">
                    <input
                      type="checkbox"
                      checked={autoGainControl}
                      onChange={(e) => setAutoGainControl(e.target.checked)}
                    />
                    Auto Gain Control
                  </label>
                </div>

                <div className="us-card">
                  <div className="us-card-title">Mic Test</div>
                  <div className="settings-mic-test">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() =>
                        micTestActive
                          ? stopMicTest()
                          : setMicTestActive(true)
                      }
                    >
                      {micTestActive ? "Stop Test" : "Start Test"}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={runMicCheck}
                      disabled={micTestRecording}
                    >
                      {micTestRecording ? "Recording..." : "Let's Check"}
                    </button>
                    <div className="mic-level">
                      <div
                        className="mic-level-fill"
                        style={{
                          width: `${Math.min(100, Math.round(micTestLevel * 240))}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="settings-help">
                    Speak and adjust sensitivity so normal speech is steady
                    without peaking.
                  </div>
                  {micTestPlaybackUrl && (
                    <audio
                      className="mic-playback"
                      controls
                      src={micTestPlaybackUrl}
                    />
                  )}
                </div>

                <div className="us-card">
                  <div className="us-card-title">Input Sensitivity</div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={Math.round(inputSensitivity * 100)}
                    onChange={(e) =>
                      setInputSensitivity(Number(e.target.value) / 100)
                    }
                    className="settings-range"
                  />
                  <div className="settings-range-labels">
                    <span>Low</span>
                    <span>{Math.round(inputSensitivity * 100)}%</span>
                    <span>High</span>
                  </div>
                  <div className="settings-help">
                    Higher sensitivity picks up quieter sounds.
                  </div>
                </div>

                <div className="us-card">
                  <div className="us-card-title">Devices</div>
                  <label>
                    Input Device
                    <select
                      className="settings-select"
                      value={inputDeviceId}
                      onChange={(e) => setInputDeviceId(e.target.value)}
                      disabled={!navigator.mediaDevices?.enumerateDevices}
                    >
                      <option value="default">Default</option>
                      {inputDevices
                        .filter((d) => d.deviceId !== "default" && d.deviceId !== "")
                        .map((device, index) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {renderDeviceLabel(device, index, "input")}
                          </option>
                        ))}
                    </select>
                  </label>

                  <label>
                    Output Device
                    <select
                      className="settings-select"
                      value={outputDeviceId}
                      onChange={(e) => setOutputDeviceId(e.target.value)}
                      disabled={!canSelectOutput}
                    >
                      <option value="default">Default</option>
                      {outputDevices
                        .filter((d) => d.deviceId !== "default" && d.deviceId !== "")
                        .map((device, index) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {renderDeviceLabel(device, index, "output")}
                          </option>
                        ))}
                    </select>
                    {!canSelectOutput && (
                      <div className="settings-help">
                        Output selection isn&apos;t supported in this browser.
                      </div>
                    )}
                    {deviceError && (
                      <div className="settings-help">{deviceError}</div>
                    )}
                  </label>
                </div>

                <div className="us-card">
                  <div className="us-card-title">Voice Sounds</div>
                  <div className="settings-help" style={{ marginBottom: 12 }}>
                    Custom sounds that play for others when you join or leave a voice channel. Max 5 seconds.
                  </div>
                  {soundError && <div className="settings-help" style={{ color: 'var(--danger)', marginBottom: 8 }}>{soundError}</div>}

                  {(['join', 'leave'] as const).map((type) => {
                    const url = type === 'join' ? preferences?.joinSoundUrl : preferences?.leaveSoundUrl;
                    const inputRef = type === 'join' ? joinSoundInputRef : leaveSoundInputRef;
                    const label = type === 'join' ? 'Join Sound' : 'Leave Sound';
                    return (
                      <div key={type} className="sound-upload-row">
                        <span className="sound-label">{label}</span>
                        <div className="sound-controls">
                          {url ? (
                            <>
                              <audio
                                className="sound-preview"
                                controls
                                src={url.startsWith('http') ? url : `${getApiBase()}${url}`}
                              />
                              <button
                                type="button"
                                className="btn-small btn-danger"
                                disabled={soundUploading !== null}
                                onClick={() => handleSoundRemove(type)}
                              >
                                {soundUploading === type ? '...' : 'Remove'}
                              </button>
                            </>
                          ) : (
                            <span className="sound-none">Default</span>
                          )}
                          <button
                            type="button"
                            className="btn-small"
                            disabled={soundUploading !== null}
                            onClick={() => inputRef.current?.click()}
                          >
                            {soundUploading === type ? 'Uploading...' : 'Upload'}
                          </button>
                          <input
                            ref={inputRef}
                            type="file"
                            accept=".mp3,.wav,.ogg,.flac,.m4a"
                            style={{ display: 'none' }}
                            onChange={(e) => handleSoundFileSelect(type, e)}
                          />
                        </div>
                        {trimmerFile && trimmerType === type && (
                          <AudioTrimmer
                            file={trimmerFile}
                            maxDuration={uploadLimits.soundMaxDurationSeconds ?? 5}
                            onConfirm={handleTrimmerConfirm}
                            onCancel={handleTrimmerCancel}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                {isElectron() && (
                  <div className="us-card">
                    <div className="us-card-title">Notifications</div>
                    <label className="settings-toggle">
                      <input
                        type="checkbox"
                        checked={nativeDesktopNotificationsEnabled}
                        onChange={(e) => setNativeDesktopNotificationsEnabled(e.target.checked)}
                      />
                      Show native OS notifications
                    </label>
                    <div className="settings-help">
                      Disable if your system's notification daemon is causing the app to hang. In-app toast notifications will still appear.
                    </div>
                    <label className="settings-toggle">
                      <input
                        type="checkbox"
                        checked={voiceChatDesktopNotify}
                        onChange={(e) => setVoiceChatDesktopNotify(e.target.checked)}
                      />
                      Desktop notifications for voice chat messages
                    </label>
                    <div className="settings-help">
                      Show a desktop notification when someone sends a message in voice chat and the app is not focused.
                    </div>
                  </div>
                )}

                <VoiceDebugPanel />
              </>
            )}

            {activeTab === "video" && (
              <>
                <div className="us-card">
                  <div className="us-card-title">Camera</div>
                  <label>
                    Camera Device
                    <select
                      className="settings-select"
                      value={cameraDeviceId}
                      onChange={(e) => setCameraDeviceId(e.target.value)}
                      disabled={!navigator.mediaDevices?.enumerateDevices}
                    >
                      <option value="default">Default</option>
                      {videoDevices
                        .filter((d) => d.deviceId !== "default" && d.deviceId !== "")
                        .map((device, index) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${index + 1}`}
                          </option>
                        ))}
                    </select>
                  </label>
                </div>

                <div className="us-card">
                  <div className="us-card-title">Screen Share</div>
                  <label>
                    Quality
                    <select
                      className="settings-select"
                      value={screenShareQuality}
                      onChange={(e) => setScreenShareQuality(e.target.value as typeof screenShareQuality)}
                    >
                      {SCREEN_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label} — {opt.detail}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="us-hint">
                    Higher tiers need more upload bandwidth and CPU. Changes apply to the
                    next share, or immediately if you are already sharing.
                  </p>
                </div>

                <div className="us-card">
                  <div className="us-card-title">Preview</div>
                  <div className="settings-camera-test">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() =>
                        cameraTestActive
                          ? stopCameraTest()
                          : startCameraTest()
                      }
                    >
                      {cameraTestActive ? "Stop Preview" : "Test Camera"}
                    </button>
                  </div>
                  {cameraTestActive && (
                    <div className="camera-preview-container">
                      <video
                        ref={cameraVideoRef}
                        autoPlay
                        playsInline
                        muted
                        className="camera-preview-video"
                      />
                    </div>
                  )}
                  {!cameraTestActive && (
                    <div className="camera-preview-placeholder">
                      <span>No preview</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === "keybinds" && (
              <>
                <div className="us-card">
                  <div className="us-card-title">Voice Shortcuts</div>
                  {([
                    { id: "toggleMute", label: "Toggle Mute", bind: keybindToggleMute },
                    { id: "toggleDeafen", label: "Toggle Deafen", bind: keybindToggleDeafen },
                    { id: "disconnect", label: "Disconnect", bind: keybindDisconnect },
                  ] as const).map(({ id, label, bind }) => (
                    <div className="keybind-row" key={id}>
                      <span className="keybind-label">{label}</span>
                      <button
                        className={`keybind-capture${capturingKeybind === id ? " recording" : ""}`}
                        onClick={() => setCapturingKeybind(capturingKeybind === id ? null : id)}
                      >
                        {capturingKeybind === id ? "Press keys..." : formatKeybind(bind)}
                      </button>
                      {bind !== keybindDefaults[id] && (
                        <button
                          className="keybind-reset"
                          onClick={() => keybindSetters[id](keybindDefaults[id])}
                          title="Reset to default"
                        >
                          ↺
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="us-card-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setKeybindToggleMute("mod+shift+m");
                      setKeybindToggleDeafen("mod+shift+d");
                      setKeybindDisconnect("mod+shift+e");
                    }}
                  >
                    Reset All
                  </button>
                </div>
              </>
            )}

            {activeTab === "cosmetics" && (
              <>
                {cosmeticError && <div className="admin-error">{cosmeticError}</div>}
                {cosmeticsLoading && (
                  <div className="admin-loading">Loading your cosmetics...</div>
                )}
                {cosmeticsLoaded && myCosmetics.length === 0 && (
                  <div className="us-card">
                    <div className="admin-empty">You don't have any cosmetics yet.</div>
                  </div>
                )}
                {cosmeticsLoaded && myCosmetics.length > 0 && (
                  <>
                    <div className="cosmetic-filter-bar" style={{ marginBottom: 12 }}>
                      <select value={cosmeticTypeFilter} onChange={(e) => setCosmeticTypeFilter(Number(e.target.value))}>
                        <option value={-1}>All Types</option>
                        <option value={0}>Nameplate</option>
                        <option value={1}>Message Style</option>
                        <option value={2}>Profile Effect</option>
                        <option value={3}>Avatar Decoration</option>
                      </select>
                    </div>
                    <div className="cosmetics-equip-grid">
                      {filteredMyCosmetics.map((uc) => (
                        <div
                          key={uc.item.id}
                          className={`cosmetic-equip-card${uc.isEquipped ? ' equipped' : ''}`}
                        >
                          <div className="cosmetic-equip-card-header">
                            <span className="cosmetic-card-name">{uc.item.name}</span>
                            <span className="cosmetic-card-rarity" style={{ background: `${CosmeticRarityColors[uc.item.rarity]}22`, color: CosmeticRarityColors[uc.item.rarity] }}>
                              {CosmeticRarityNames[uc.item.rarity]}
                            </span>
                          </div>
                          <div className="cosmetic-card-type">{CosmeticTypeNames[uc.item.type]}</div>
                          <div className="cosmetic-card-preview">
                            {renderCosmeticPreview(uc.item)}
                          </div>
                          {uc.item.description && <div className="cosmetic-card-desc">{uc.item.description}</div>}
                          <div className="cosmetic-equip-action">
                            {uc.isEquipped ? (
                              <button className="btn-secondary cosmetic-unequip-btn" onClick={() => handleUnequip(uc.item.id)}>
                                Unequip
                              </button>
                            ) : (
                              <button className="cosmetic-equip-btn" onClick={() => handleEquip(uc.item.id)}>
                                Equip
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {activeTab === "connections" && (
              <div className="us-card">
                <div className="us-card-title">XenForo Forum</div>
                <p className="settings-help" style={{ marginTop: 0, marginBottom: 12 }}>
                  Link your XenForo account so messages you create forum topics from
                  are posted as you on the forum.
                </p>
                {xfError && <div className="auth-error">{xfError}</div>}
                {!xfConnectionLoaded ? (
                  <div className="settings-help">Loading…</div>
                ) : xfConnection ? (
                  <div className="xf-connection-row">
                    <div className="xf-connection-info">
                      <span className="xf-connection-status">Linked</span>
                      <span className="xf-connection-name">{xfConnection.xfUsername}</span>
                      <span className="xf-connection-meta">
                        since {new Date(xfConnection.linkedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn-danger"
                      disabled={xfBusy}
                      onClick={handleUnlinkXenForo}
                    >
                      {xfBusy ? 'Unlinking…' : 'Unlink'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="xf-link-btn"
                    onClick={handleLinkXenForo}
                  >
                    Link XenForo Account
                  </button>
                )}
              </div>
            )}

            {activeTab === "app" && (
              <div className="us-card">
                <div className="us-card-title">Launch on Startup</div>
                <p className="settings-help" style={{ marginTop: 0, marginBottom: 12 }}>
                  Automatically start Abyss when you log in to your computer.
                </p>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={autoLaunchEnabled}
                    disabled={autoLaunchLoading}
                    onChange={(e) => handleAutoLaunchToggle(e.target.checked)}
                  />
                  Launch Abyss on system startup
                </label>
              </div>
            )}

            {activeTab === "server" && (
              <>
                {recentInstances.length > 0 && (
                  <div className="us-card">
                    <div className="us-card-title">Recent Instances</div>
                    <div className="recent-instances-settings">
                      {recentInstances.map((instance) => (
                        <div key={instance.url} className="recent-instance-item">
                          <div className="recent-instance-info">
                            <div className="recent-instance-name">
                              {instance.nickname || new URL(instance.url).hostname}
                            </div>
                            <div className="recent-instance-url">{instance.url}</div>
                          </div>
                          <div className="recent-instance-actions">
                            {instance.url !== serverUrl && (
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => handleServerUrlSave(instance.url)}
                                disabled={serverUrlSaving}
                              >
                                Switch
                              </button>
                            )}
                            {instance.url === serverUrl && (
                              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Current</span>
                            )}
                            <button
                              type="button"
                              className="btn-danger-secondary"
                              onClick={() => removeRecentInstance(instance.url)}
                              disabled={serverUrlSaving}
                              title="Remove from recent"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="us-card">
                  <div className="us-card-title">Instance Configuration</div>
                  <p className="settings-help" style={{ marginTop: 0, marginBottom: 12 }}>
                    Configure the Abyss instance you want to connect to. Change this if you're self-hosting or connecting to a different instance.
                  </p>
                  <label>
                    Instance URL
                    <input
                      type="text"
                      value={serverUrlInput}
                      onChange={(e) => setServerUrlInput(e.target.value)}
                      placeholder="https://abyss.example.com"
                      disabled={serverUrlSaving}
                    />
                  </label>
                  {serverUrlError && (
                    <div className="settings-help" style={{ color: 'var(--danger)', marginTop: 8 }}>
                      {serverUrlError}
                    </div>
                  )}
                  <div className="settings-help" style={{ marginTop: 8 }}>
                    Current instance: {serverUrl || 'Using default configuration'}
                  </div>
                </div>

                <div className="us-card-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => setServerUrlInput(serverUrl || import.meta.env.VITE_API_URL || '')}
                    disabled={serverUrlSaving}
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => handleServerUrlSave()}
                    disabled={serverUrlSaving || serverUrlInput === serverUrl}
                  >
                    {serverUrlSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}

            {activeTab === "account" && (
              <div className="us-card">
                <div className="us-card-title">Log Out</div>
                <p className="settings-help" style={{ marginTop: 0, marginBottom: 12 }}>
                  Sign out of this device.
                </p>
                <button className="btn-danger" type="button" onClick={logout}>
                  Log Out
                </button>
              </div>
            )}
    </SettingsModal>
  );
}
